import { get } from 'lodash';
import { useAtomValue } from 'jotai';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import useFlowQuery, { createQuery } from '../../hooks/useFlowQuery';
import useQueryParams from '../../hooks/useQueryParams';
import { appendFlowIdentifier } from '@utils/filter';
import { panelsAtom } from '../../atoms';
import { getFlowMetrics } from '@utils/flow/utils';

function useAppFlowFetch({ flowId, flowMeta }: { flowId: string; flowMeta?: any }) {
  const { identifier } = flowMeta || {};
  const panels = useAtomValue(panelsAtom);

  const metrics = getFlowMetrics(flowMeta);
  const { interval, filter, _rKey, allowedMetrics } = useQueryParams();

  const isDeleted = flowMeta?.isDeleted;

  const filteredMetrics = metrics.filter(
    (metric) =>
      (metric && allowedMetrics && metric in allowedMetrics) ||
      (metric &&
        allowedMetrics &&
        getInvertedPercentageMetricByKey({ metricKey: metric }) in allowedMetrics),
  );

  const allowedMetricList = [...new Set([...filteredMetrics])];
  const options = {};
  const payload = createQuery(
    allowedMetricList,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    appendFlowIdentifier(filter, identifier) as any,
    options,
  );

  const result = useFlowQuery({
    flowId,
    queryKey: ['overview', 'flow', payload.queries, _rKey],
    queryFn: () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        const formattedData = formatTimeseriesData({
          metrics: allowedMetricList,
          data,
          interval,
          annotations,
        });
        return { data: formattedData, totals };
      }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevent refetch when panel is shown
      enabled: !!metrics.length && !isDeleted && panels.flows,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 5 * 60 * 1000, // 5m
    },
  });

  if (isDeleted) {
    return {
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve(),
      queued: false,
    };
  }

  return result;
}

export default useAppFlowFetch;
